import React, { useState, useEffect } from "react";
import { ArrowLeft, Star, MoreHorizontal, Trash2, Edit2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ChatConversation } from "./ChatConversation";
import { ChatProjectSettingsPanel } from "./ChatProjectSettingsPanel";
import { ChatConversationsList } from "./ChatConversationsList";

interface ChatProject {
  id: string;
  name: string;
  description: string | null;
  is_starred: boolean;
}

interface ChatProjectViewProps {
  projectId: string;
  onBack: () => void;
}

export const ChatProjectView: React.FC<ChatProjectViewProps> = ({ projectId, onBack }) => {
  const [project, setProject] = useState<ChatProject | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      setProject(data);
      setEditedName(data.name);
      setEditedDescription(data.description || "");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      onBack();
    }
  };

  const toggleStar = async () => {
    if (!project) return;
    
    try {
      const { error } = await supabase
        .from("chat_projects")
        .update({ is_starred: !project.is_starred })
        .eq("id", projectId);

      if (error) throw error;
      setProject({ ...project, is_starred: !project.is_starred });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateName = async () => {
    if (!project || !editedName.trim()) return;
    
    try {
      const { error } = await supabase
        .from("chat_projects")
        .update({ name: editedName.trim() })
        .eq("id", projectId);

      if (error) throw error;
      setProject({ ...project, name: editedName.trim() });
      setIsEditingName(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateDescription = async () => {
    if (!project) return;
    
    try {
      const { error } = await supabase
        .from("chat_projects")
        .update({ description: editedDescription.trim() || null })
        .eq("id", projectId);

      if (error) throw error;
      setProject({ ...project, description: editedDescription.trim() || null });
      setIsEditingDescription(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteProject = async () => {
    try {
      const { error } = await supabase
        .from("chat_projects")
        .delete()
        .eq("id", projectId);

      if (error) throw error;
      toast({
        title: "Project deleted",
      });
      onBack();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleNewConversation = () => {
    // Set to 'new' to trigger creating a new conversation
    setSelectedConversationId('new');
  };

  const handleConversationCreated = (conversationId: string) => {
    setSelectedConversationId(conversationId);
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show conversation view if one is selected or starting new
  if (selectedConversationId) {
    return (
      <ChatConversation
        conversationId={selectedConversationId === 'new' ? null : selectedConversationId}
        projectId={projectId}
        projectName={project.name}
        onBack={() => setSelectedConversationId(null)}
        onConversationCreated={handleConversationCreated}
      />
    );
  }

  return (
    <div className="flex h-full bg-background">
      {/* Left Column - Project Overview */}
      <div className="flex-1 flex flex-col border-r border-border">
        {/* Back navigation */}
        <div className="p-6 pb-0">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            All projects
          </button>
        </div>

        {/* Project header */}
        <div className="p-6 pb-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              {isEditingName ? (
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={updateName}
                  onKeyDown={(e) => e.key === "Enter" && updateName()}
                  className="text-3xl font-semibold h-auto py-1 px-2 -ml-2"
                  autoFocus
                />
              ) : (
                <h1 
                  className="text-3xl font-semibold cursor-pointer hover:text-muted-foreground transition-colors"
                  onClick={() => setIsEditingName(true)}
                >
                  {project.name}
                </h1>
              )}
            </div>
            
            <div className="flex items-center gap-1 ml-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-5 w-5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsEditingName(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={deleteProject} className="text-destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete project
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="icon" onClick={toggleStar} className="h-8 w-8">
                <Star
                  className={`h-5 w-5 ${project.is_starred ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
                />
              </Button>
            </div>
          </div>

          {/* Description */}
          {isEditingDescription ? (
            <Input
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              onBlur={updateDescription}
              onKeyDown={(e) => e.key === "Enter" && updateDescription()}
              placeholder="Add a description..."
              className="text-muted-foreground"
              autoFocus
            />
          ) : (
            <p 
              className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              onClick={() => setIsEditingDescription(true)}
            >
              {project.description || "Click to add a description..."}
            </p>
          )}
        </div>

        {/* Chat Input Card */}
        <div className="px-6 pb-6">
          <div 
            className="bg-card border border-border rounded-xl p-4 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={handleNewConversation}
          >
            <ChatConversation
              conversationId={null}
              projectId={projectId}
              projectName={project.name}
              onConversationCreated={handleConversationCreated}
              isCompact
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 px-6 pb-6 overflow-hidden">
          <ChatConversationsList
            projectId={projectId}
            selectedConversationId={selectedConversationId}
            onSelectConversation={setSelectedConversationId}
          />
        </div>
      </div>

      {/* Right Column - Settings Panel */}
      <div className="w-80 shrink-0 bg-muted/30 overflow-auto">
        <ChatProjectSettingsPanel projectId={projectId} />
      </div>
    </div>
  );
};